import { QuickPassNotification } from '../_types/quickpass-notification'
import { ONE_DAY_MS, ONE_HOUR_MS } from '../_utilities/relative-time'

/**
 * Builds a representative notifications list for the dev/local fallback,
 * so the bell + drawer have realistic content to render before the
 * backend is wired up. Mix of types, mix of read/unread, spread across
 * a few days so relative-time formatting can be exercised.
 *
 * @param {string} quickPassUserId - the QuickPass user the seed is scoped to
 * @returns {QuickPassNotification[]} a deterministic seed list, newest first
 */
const buildSeedNotifications = (
  quickPassUserId: string,
): QuickPassNotification[] => {
  const now = Date.now()

  return [
    {
      id: 'seed_price_drop_1',
      quickPassUserId,
      type: 'imported-listing-price-dropped',
      title: "Price dropped on a place you're watching",
      body: 'The Liberty Village two-bedroom you imported is now $2,395 — down from $2,495. Worth a second look?',
      linkPath: '/imported-listings/imported_seed_liberty_two_bed',
      data: {
        listingId: 'imported_seed_liberty_two_bed',
        previousPrice: 2495,
        currentPrice: 2395,
      },
      isRead: false,
      createdAt: now - 3 * ONE_HOUR_MS,
      readAt: null,
    },
    {
      id: 'seed_available_1',
      quickPassUserId,
      type: 'imported-listing-became-available',
      title: 'A place you saved is back on the market',
      body: 'The Junction loft was marked unavailable last week and just relisted. Move fast — these go quick around your budget.',
      linkPath: '/imported-listings/imported_seed_junction_loft',
      data: {
        listingId: 'imported_seed_junction_loft',
      },
      isRead: false,
      createdAt: now - 9 * ONE_HOUR_MS,
      readAt: null,
    },
    {
      id: 'seed_price_increase_1',
      quickPassUserId,
      type: 'imported-listing-price-increased',
      title: 'Heads up — a price went up',
      body: 'The Bloor West bungalow climbed from $2,800 to $2,950 overnight. Adjusting your budget or filtering it out?',
      linkPath: '/imported-listings/imported_seed_bloor_bungalow',
      data: {
        listingId: 'imported_seed_bloor_bungalow',
        previousPrice: 2800,
        currentPrice: 2950,
      },
      isRead: true,
      createdAt: now - 1 * ONE_DAY_MS - 4 * ONE_HOUR_MS,
      readAt: now - 1 * ONE_DAY_MS,
    },
    {
      id: 'seed_unavailable_1',
      quickPassUserId,
      type: 'imported-listing-became-unavailable',
      title: 'Marked rented',
      body: "The Roncesvalles condo got pulled. We'll keep watching in case it relists, but it's probably gone.",
      linkPath: '/imported-listings/imported_seed_roncesvalles_condo',
      data: {
        listingId: 'imported_seed_roncesvalles_condo',
      },
      isRead: true,
      createdAt: now - 2 * ONE_DAY_MS,
      readAt: now - 2 * ONE_DAY_MS + 30 * 60 * 1000,
    },
    {
      id: 'seed_scrape_failing_1',
      quickPassUserId,
      type: 'imported-listing-scrape-failing',
      title: "Couldn't refresh one of your listings",
      body: 'The Beaches duplex listing has been throwing scrape errors for three days running. The site may have taken it down — try opening it manually.',
      linkPath: '/imported-listings/imported_seed_beaches_duplex',
      data: {
        listingId: 'imported_seed_beaches_duplex',
      },
      isRead: true,
      createdAt: now - 3 * ONE_DAY_MS,
      readAt: now - 3 * ONE_DAY_MS + 2 * ONE_HOUR_MS,
    },
    {
      id: 'seed_system_welcome',
      quickPassUserId,
      type: 'system',
      title: 'Welcome to QuickPass',
      body: "Your portable renter profile is ready. Save listings, paste URLs from any rental site, and we'll keep you posted when things change.",
      linkPath: '/dashboard',
      data: {},
      isRead: true,
      createdAt: now - 5 * ONE_DAY_MS,
      readAt: now - 5 * ONE_DAY_MS + 5 * 60 * 1000,
    },
  ]
}

export { buildSeedNotifications }
